import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap, Crown } from 'lucide-react';
import { AppButton } from '../ui';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import { usePlan } from '../../contexts/PlanContext';

// No stylesheet. This component was converted to Tailwind utilities on 2026-08-21
// as the Phase 1.0b pilot — see docs/design-system/DESIGN_SYSTEM_TODO.md.
// LockedFeature.scss was deleted in the same commit (the atomic-migration rule,
// TOKEN_CONTRACT §6.1); its sidebar rules moved to styles/shell/_sidebar.scss,
// which is where they always belonged.

/**
 * Feature-key → human-readable label map.
 * Keep in sync with Plan model boolean fields.
 */
const FEATURE_LABELS = {
  quizAssessment: 'Quiz & Assessments',
  aiMock: 'AI Mock Interview',
  aiLearning: 'AI Learning',
  aiQuestionBank: 'AI Question Bank',
  feeManagement: 'Fee Management',
  enquiryManagement: 'Enquiry Management',
  interviews: 'Interviews',
  codingPractice: 'Coding Practice',
  selfPacedCourses: 'Self-Paced Learning',
  assignments: 'Assignments',
  customBranding: 'Custom Branding',
  customDomain: 'Custom Domain',
  apiAccess: 'API Access',
  enquiryFormBuilder: 'Enquiry Form Builder',
  zoomV2: 'Zoom Integration',
  recordings: 'Class Recordings',
  whatsapp: 'WhatsApp Notifications',
  platformHostedZoom: 'Platform Hosted Zoom',
  gratsMeet: 'Grats Meet Live Classes',
  cloudTelephony: 'Cloud Telephony',
  adsManager: 'Marketing & Ads',
  leadCapture: 'Lead Capture & Sync',
};

const LockedFeaturePage = ({ featureKey, featureLabel }) => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isAdmin = user?.usertype === 'superadmin' || user?.usertype === 'admin';
  const label = featureLabel || FEATURE_LABELS[featureKey] || featureKey;
  // A distributor's client never buys a plan — its distributor manages it.
  const { managedBy } = usePlan();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(!managedBy);

  useEffect(() => {
    if (!featureKey || managedBy) return;
    apiClient.get(`/billing/plans-for-feature?feature=${encodeURIComponent(featureKey)}`)
      .then(res => setPlans(res.data?.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [featureKey, managedBy]);

  const formatPrice = (price) => {
    if (!price) return 'Free';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <div className="tw-flex tw-min-h-[60vh] tw-items-center tw-justify-center tw-px-4 tw-py-8">
      <div className="tw-w-full tw-max-w-[480px] tw-rounded-lg tw-border tw-border-line tw-bg-surface-raised tw-px-8 tw-pb-9 tw-pt-12 tw-text-center tw-shadow-card-lg">
        <div className="tw-mb-5 tw-inline-flex tw-h-[72px] tw-w-[72px] tw-items-center tw-justify-center tw-rounded-full tw-bg-warning-soft tw-text-warning">
          <Lock size={32} />
        </div>
        <h2 className="tw-mb-2.5 tw-text-[22px] tw-font-medium tw-text-content">
          {label} is locked
        </h2>
        <p className="tw-mb-7 tw-text-sm tw-leading-relaxed tw-text-content-muted">
          This feature is not available on your current plan.
          {managedBy
            ? (isAdmin
              ? ` Your plan is managed by ${managedBy.name} — ask them to enable it.`
              : ' Contact your institute administrator.')
            : (isAdmin
              ? ' Upgrade your plan to unlock it.'
              : ' Contact your institute administrator to upgrade the plan.')}
        </p>

        {isAdmin && plans.length > 0 && (
          <div className="tw-mb-7 tw-rounded-md tw-border tw-border-line tw-bg-surface-alt tw-p-4 tw-text-left">
            <p className="tw-mb-3 tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-muted">
              <Crown size={16} className="tw-text-warning" /> Available in these plans
            </p>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {plans.map(plan => (
                <div
                  key={plan._id}
                  className="tw-flex tw-items-center tw-justify-between tw-rounded tw-border tw-border-line tw-bg-surface-raised tw-px-3.5 tw-py-2.5"
                >
                  <div className="tw-text-sm tw-font-semibold tw-text-content">{plan.name}</div>
                  <div className="tw-text-[15px] tw-font-medium tw-text-primary">
                    {/* Server-resolved price (vc_be utils/planPricing.js). An
                        institute on a negotiated price sees theirs here too —
                        this screen asks them to decide on an upgrade, so quoting
                        list price would be quoting a number they will not pay.
                        Falls back to the plan field for responses that predate
                        `pricing`. */}
                    {plan.pricing?.monthly?.discountAmount > 0 && (
                      <span className="tw-mr-1.5 tw-text-xs tw-font-normal tw-text-content-muted tw-line-through">
                        {formatPrice(plan.pricing.monthly.listPrice)}
                      </span>
                    )}
                    {formatPrice(plan.pricing?.monthly?.payable ?? plan.priceMonthly)}
                    {(plan.pricing?.monthly?.payable ?? plan.priceMonthly) > 0 && (
                      <span className="tw-text-xs tw-font-normal tw-text-content-muted">/mo</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="tw-mb-5 tw-text-[13px] tw-text-content-muted">Loading plans...</div>
        )}

        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
          {isAdmin && !managedBy && (
            <AppButton
              variant="primary"
              onClick={() => navigate('/billing')}
            >
              <Zap size={16} /> Upgrade Plan
            </AppButton>
          )}
          <AppButton
            variant="light"
            onClick={() => navigate(-1)}
          >
            Go Back
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default LockedFeaturePage;
